import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { computed, DestroyRef, inject, Inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { catchError, defer, EMPTY, finalize, mergeMap, Observable, of } from 'rxjs';

import { ProjectAnswerStatus } from '@empathy/global/data';
import { GenerateUUID } from '@empathy/global/utils';
import { SourceVariables, SurveyFeedback, SurveyProgress, SurveySettings } from '@empathy/survey/base';
import {
  ISurveyNavigationResponse,
  retrySurveyNavigation,
  SurveyCompleteResponse,
  SurveyNavigationContract,
  SurveyViewerService
} from '@empathy/survey-viewer';

import { PreviewMediaStore } from './preview-media-store.service';
import { SurveyPreviewGenerationCoordinator } from './survey-preview-generation-coordinator.service';
import { EnvironmentConfig } from '../config/environment.config';
import { SurveyPreviewBackRequest, SurveyPreviewResult, SurveyPreviewSaveRequest, SurveyPreviewStartRequest } from '../models/survey-preview-api.interface';
import { SurveyPreviewError } from '../models/survey-preview-error.interface';
import { SurveyPreviewRun } from '../models/survey-preview-run.interface';

@Injectable()
export class AdminSurveyPreviewNavigationService implements SurveyNavigationContract {
  readonly error$ = signal<SurveyPreviewError | null>(null);
  readonly isStarting$ = signal(false);
  readonly isGoingBack$ = signal(false);
  readonly isSaving$ = signal(false);
  readonly isCompleting$ = signal(false);
  readonly isBusy$ = computed(() => this.isStarting$() || this.isGoingBack$() || this.isSaving$() || this.isCompleting$());
  readonly previewRun$ = signal<SurveyPreviewRun | null>(null);

  private readonly destroyRef = inject(DestroyRef);
  private readonly generationCoordinator = inject(SurveyPreviewGenerationCoordinator);
  private readonly http = inject(HttpClient);
  private readonly previewMediaStore = inject(PreviewMediaStore);
  private readonly surveyViewerService = inject(SurveyViewerService);

  private currentResponse: SurveyPreviewResult | null = null;
  private generation: number | null = null;
  private previewSessionId: string | null = null;
  private started = false;
  private version: number | null = null;

  constructor(@Inject('environment') private readonly environment: EnvironmentConfig) {}

  start(
    authoredSurveyJson: string,
    variables: SourceVariables | null,
    generation: number,
    languageCode = 'en-GB',
    replacesPreviewSessionId?: string
  ): Observable<string> {
    if (this.started) {
      return EMPTY;
    }

    this.started = true;
    this.generation = generation;
    this.error$.set(null);
    this.isStarting$.set(true);

    const request: SurveyPreviewStartRequest = {
      surveyJson: authoredSurveyJson,
      ...(variables ? { variables } : {}),
      languageCode,
      ...(replacesPreviewSessionId ? { replacesPreviewSessionId } : {})
    };

    return this.http.post<SurveyPreviewResult>(`${this.environment.surveyEndPoint}preview/start`, request).pipe(
      mergeMap((response) => {
        if (!this.isActiveGeneration(generation)) {
          return EMPTY;
        }

        const previewRun = this.mapStartResponse(response, generation);
        if (!previewRun || !this.acceptResponse(response, generation)) {
          return EMPTY;
        }

        this.previewRun$.set(previewRun);
        return of(response.previewSessionId);
      }),
      catchError((error: unknown) => this.handlePreviewError(error, generation)),
      finalize(() => this.isStarting$.set(false)),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  goNext(): Observable<ISurveyNavigationResponse> {
    const navigationContext = this.getNavigationContext();
    if (!navigationContext) {
      return EMPTY;
    }

    const [generation, previewSessionId, version] = navigationContext;
    const progress = this.mapProgress();
    const request: SurveyPreviewSaveRequest = {
      previewSessionId,
      expectedVersion: version,
      operationId: GenerateUUID(),
      answerJson: progress.answerJson,
      submittedQuestionIds: progress.submittedQuestionIds,
      terminatedQuestionId: progress.terminatedQuestionId
    };

    this.error$.set(null);
    this.isSaving$.set(true);
    return this.http.post<SurveyPreviewResult>(`${this.environment.surveyEndPoint}preview/save`, request).pipe(
      retrySurveyNavigation(),
      mergeMap((response) => this.mapNavigationResponse(response, generation)),
      catchError((error: unknown) => this.handlePreviewError(error, generation)),
      finalize(() => this.isSaving$.set(false)),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  goBack(currentPageId: string): Observable<ISurveyNavigationResponse> {
    const navigationContext = this.getNavigationContext();
    if (!navigationContext) {
      return EMPTY;
    }

    const [generation, previewSessionId, version] = navigationContext;
    const request: SurveyPreviewBackRequest = {
      previewSessionId,
      expectedVersion: version,
      operationId: GenerateUUID(),
      expectedCurrentPageId: currentPageId
    };

    this.error$.set(null);
    this.isGoingBack$.set(true);
    return this.http.post<SurveyPreviewResult>(`${this.environment.surveyEndPoint}preview/back`, request).pipe(
      retrySurveyNavigation(),
      mergeMap((response) => this.mapNavigationResponse(response, generation)),
      catchError((error: unknown) => this.handlePreviewError(error, generation)),
      finalize(() => this.isGoingBack$.set(false)),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  complete(): Observable<SurveyCompleteResponse> {
    return defer(() => {
      this.isCompleting$.set(true);
      return of({
        statusId: ProjectAnswerStatus.Incomplete,
        pointsAwarded: 0,
        ticketsAwarded: 0,
        redirectUrl: ''
      }).pipe(finalize(() => this.isCompleting$.set(false)));
    });
  }

  sendFeedback(_inviteId: string, _feedback: SurveyFeedback): Observable<boolean | null> {
    return of(null);
  }

  showCommunicationError(): void {
    if (this.generation !== null && this.isActiveGeneration(this.generation)) {
      this.error$.set({
        kind: 'unavailable',
        message: 'Preview media could not be loaded. Start again to continue.',
        canRestart: true
      });
    }
  }

  private acceptResponse(response: SurveyPreviewResult, generation: number): boolean {
    if (!this.isActiveGeneration(generation)) {
      return false;
    }

    if (this.previewSessionId && response.previewSessionId !== this.previewSessionId) {
      this.showInvalidResponseError();
      return false;
    }

    if (!response.previewSessionId || !Number.isInteger(response.version) || response.version < 1 || (!response.endedInd && !response.currentPage)) {
      this.showInvalidResponseError();
      return false;
    }

    if (!this.reconcilePreviewMedia(response.existingAnswerJson)) {
      this.showInvalidResponseError();
      return false;
    }

    this.previewSessionId = response.previewSessionId;
    this.version = response.version;
    this.currentResponse = response;
    this.surveyViewerService.canGoBackInd$.set(response.canGoBackInd);
    return true;
  }

  private getNavigationContext(): [number, string, number] | null {
    if (this.isBusy$() || this.generation === null || this.previewSessionId === null || this.version === null || !this.isActiveGeneration(this.generation)) {
      return null;
    }

    return [this.generation, this.previewSessionId, this.version];
  }

  private collectPreviewMediaIds(value: unknown, retainedIds: Set<string>): void {
    if (typeof value === 'string') {
      if (value.startsWith(PreviewMediaStore.PREFIX)) {
        retainedIds.add(value);
        return;
      }

      try {
        this.collectPreviewMediaIds(JSON.parse(value) as unknown, retainedIds);
      } catch {
        return;
      }
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((entry) => this.collectPreviewMediaIds(entry, retainedIds));
      return;
    }

    if (value && typeof value === 'object') {
      Object.values(value).forEach((entry) => this.collectPreviewMediaIds(entry, retainedIds));
    }
  }

  private extractServerMessage(error: HttpErrorResponse): string {
    const responseError = error.error as { message?: string | string[] } | string | null;
    if (typeof responseError === 'string') {
      return responseError;
    }

    if (Array.isArray(responseError?.message)) {
      return responseError.message.join(' ');
    }

    return responseError?.message ?? '';
  }

  private handlePreviewError(error: unknown, generation: number): Observable<never> {
    if (!this.isActiveGeneration(generation)) {
      return EMPTY;
    }

    this.error$.set(this.mapPreviewError(error));
    return EMPTY;
  }

  private isActiveGeneration(generation: number): boolean {
    return this.generation === generation && this.generationCoordinator.isActive(generation);
  }

  private mapNavigationResponse(response: SurveyPreviewResult, generation: number): Observable<ISurveyNavigationResponse> {
    if (!this.acceptResponse(response, generation)) {
      return EMPTY;
    }

    return of({
      statusId: ProjectAnswerStatus.Incomplete,
      redirectUrl: '',
      existingAnswerJson: response.existingAnswerJson,
      currentPage: response.endedInd ? null : response.currentPage,
      progress: response.progress,
      canGoBackInd: response.canGoBackInd
    });
  }

  private mapPreviewError(error: unknown): SurveyPreviewError {
    if (!(error instanceof HttpErrorResponse)) {
      return { kind: 'unknown', message: 'Preview could not be loaded. Start again to retry.', canRestart: true };
    }

    const serverMessage = this.extractServerMessage(error);
    if (error.status === HttpStatusCode.NotFound || (error.status === HttpStatusCode.Gone && serverMessage.startsWith('Preview expired'))) {
      return { kind: 'expired', message: 'Preview expired—start again', canRestart: true };
    }

    if (error.status === HttpStatusCode.Gone) {
      return { kind: 'unavailable', message: 'Preview session is unavailable. Start again to continue.', canRestart: true };
    }

    if (error.status === HttpStatusCode.Conflict && serverMessage === 'Preview session changed') {
      return { kind: 'version-conflict', message: 'Preview changed in another request. Start again to continue.', canRestart: true };
    }

    if (error.status === HttpStatusCode.Conflict) {
      return { kind: 'navigation-conflict', message: serverMessage || 'Preview navigation conflicted with the current session.', canRestart: true };
    }

    if (error.status === HttpStatusCode.Unauthorized || error.status === HttpStatusCode.Forbidden) {
      return { kind: 'authorization', message: 'Preview access is no longer available. Start again to continue.', canRestart: true };
    }

    if (error.status === HttpStatusCode.TooManyRequests) {
      return {
        kind: 'quota',
        message:
          'Preview limit reached. Continue in an existing preview, or close unused preview tabs and wait for those sessions to expire before trying again.',
        canRestart: true,
        restartText: 'Try Again'
      };
    }

    const snapshotTooLarge = error.error && typeof error.error === 'object' && error.error.code === 'SURVEY_SNAPSHOT_TOO_LARGE';
    if (error.status === HttpStatusCode.PayloadTooLarge || snapshotTooLarge) {
      return { kind: 'size', message: 'This survey is too large to preview. Reduce its size and try again.', canRestart: false };
    }

    if (error.status === HttpStatusCode.BadRequest) {
      return { kind: 'invalid', message: serverMessage || 'The survey preview request is invalid.', canRestart: false };
    }

    if (error.status === HttpStatusCode.ServiceUnavailable) {
      return { kind: 'unavailable', message: 'Preview is temporarily unavailable. Start again to retry.', canRestart: true };
    }

    return { kind: 'unknown', message: serverMessage || 'Preview could not be loaded. Start again to retry.', canRestart: true };
  }

  private mapProgress(): SurveyProgress {
    return new SurveyProgress(JSON.stringify(this.surveyViewerService.mapAnswers()), this.surveyViewerService.mapSubmittedQuestionIds());
  }

  private mapStartResponse(response: SurveyPreviewResult, generation: number): SurveyPreviewRun | null {
    if (!this.isActiveGeneration(generation) || !response.details?.settings || response.endedInd || !response.currentPage) {
      this.showInvalidResponseError();
      return null;
    }

    const settings: SurveySettings = { ...response.details.settings, useFeedbackInd: false };
    const endPageHtml = response.endPage?.html?.trim() ? response.endPage.html : 'Preview Completed';
    const endButtonText = response.endPage?.buttonText?.trim() ? response.endPage.buttonText : 'Start Again';

    return {
      startSurvey: {
        welcome: response.welcome,
        endPage: { html: endPageHtml, buttonText: endButtonText },
        existingAnswerJson: response.existingAnswerJson,
        currentPage: response.currentPage,
        progress: response.progress,
        canGoBackInd: response.canGoBackInd,
        languageCode: response.languageCode
      },
      settings,
      surveyTitle: response.details.title ?? ''
    };
  }

  private reconcilePreviewMedia(existingAnswerJson: string): boolean {
    const retainedIds = new Set<string>();
    if (existingAnswerJson === '') {
      this.previewMediaStore.reconcile(retainedIds);
      return true;
    }

    try {
      this.collectPreviewMediaIds(JSON.parse(existingAnswerJson) as unknown, retainedIds);
      this.previewMediaStore.reconcile(retainedIds);
      return true;
    } catch {
      return false;
    }
  }

  private showInvalidResponseError(): void {
    this.error$.set({
      kind: 'unavailable',
      message: 'Preview returned an invalid response. Start again to continue.',
      canRestart: true
    });
  }
}
